import { CafeMetadataSchema, FlowGraphMetadataSchema, FlowGraphSchema, HAConditionSchema, HATriggerSchema, isDeviceAction, isHACondition, markConditionInSequence, validateGraphStructure, } from '@cafe/shared';
import { load as yamlLoad } from 'js-yaml';
import { generateEdgeId, generateGraphId, generateNodeId } from '../utils/generateIds';
import { applyHeuristicLayout } from './layout';
// Type guards for Home Assistant objects
/** Returns true if the action is a delay node */
function isDelayAction(action) {
    return (typeof action === 'object' &&
        action !== null &&
        'delay' in action &&
        (typeof action.delay === 'string' ||
            typeof action.delay === 'number' ||
            (typeof action.delay === 'object' &&
                action.delay !== null)));
}
const DELAY_DURATION_MULTIPLIERS = {
    hours: 60 * 60 * 1000,
    minutes: 60 * 1000,
    seconds: 1000,
    milliseconds: 1,
};
function isFiniteDelayNumber(value) {
    return typeof value === 'number' && Number.isFinite(value);
}
function unwrapTemplateExpression(value) {
    const trimmed = value.trim();
    const templateMatch = /^\{\{\s*([\s\S]*?)\s*\}\}$/.exec(trimmed);
    return templateMatch ? templateMatch[1].trim() : trimmed;
}
function toDelayTemplatePart(value) {
    if (isFiniteDelayNumber(value)) {
        return String(value);
    }
    if (typeof value !== 'string' || value.trim() === '') {
        return null;
    }
    const trimmed = value.trim();
    const numericValue = Number(trimmed);
    if (Number.isFinite(numericValue)) {
        return String(numericValue);
    }
    return `(${unwrapTemplateExpression(trimmed)})`;
}
function buildTemplatedDelayString(duration) {
    const terms = Object.entries(DELAY_DURATION_MULTIPLIERS).flatMap(([key, multiplier]) => {
        const templatePart = toDelayTemplatePart(duration[key]);
        if (!templatePart) {
            return [];
        }
        return multiplier === 1 ? [templatePart] : [`(${templatePart}) * ${multiplier}`];
    });
    const totalMillisecondsExpression = terms.length > 0 ? terms.join(' + ') : '0';
    const hoursExpression = `(${totalMillisecondsExpression}) // 3600000`;
    const minutesExpression = `((${totalMillisecondsExpression}) % 3600000) // 60000`;
    const secondsExpression = `((${totalMillisecondsExpression}) % 60000) // 1000`;
    const millisecondsExpression = `(${totalMillisecondsExpression}) % 1000`;
    return Object.hasOwn(duration, 'milliseconds')
        ? `{{ '%02d:%02d:%02d.%03d' | format(${hoursExpression}, ${minutesExpression}, ${secondsExpression}, ${millisecondsExpression}) }}`
        : `{{ '%02d:%02d:%02d' | format(${hoursExpression}, ${minutesExpression}, ${secondsExpression}) }}`;
}
function normalizeDelayValue(delayValue) {
    if (typeof delayValue === 'string') {
        return delayValue;
    }
    if (isFiniteDelayNumber(delayValue)) {
        return String(delayValue);
    }
    if (typeof delayValue !== 'object' || delayValue === null) {
        return '';
    }
    const duration = delayValue;
    const normalizedDuration = {};
    let requiresTemplateNormalization = false;
    for (const key of Object.keys(DELAY_DURATION_MULTIPLIERS)) {
        const rawValue = duration[key];
        if (rawValue === undefined) {
            continue;
        }
        if (isFiniteDelayNumber(rawValue)) {
            normalizedDuration[key] = rawValue;
            continue;
        }
        if (typeof rawValue === 'string' && rawValue.trim() !== '') {
            const numericValue = Number(rawValue.trim());
            if (Number.isFinite(numericValue)) {
                normalizedDuration[key] = numericValue;
            }
            else {
                requiresTemplateNormalization = true;
            }
            continue;
        }
        requiresTemplateNormalization = true;
    }
    if (requiresTemplateNormalization) {
        return buildTemplatedDelayString(duration);
    }
    return normalizedDuration;
}
/** Returns true if the action is a wait node */
function isWaitAction(action) {
    return (typeof action === 'object' &&
        action !== null &&
        ('wait_template' in action || 'wait_for_trigger' in action));
}
/** Returns true if the action is a choose block */
function isChooseAction(action) {
    return typeof action === 'object' && action !== null && 'choose' in action;
}
/** Returns true if the action is a parallel block */
function isParallelAction(action) {
    return (typeof action === 'object' &&
        action !== null &&
        'parallel' in action &&
        Array.isArray(action.parallel));
}
/** Returns true if the action is an if/then/else block */
function isIfThenAction(action) {
    return (typeof action === 'object' &&
        action !== null &&
        'if' in action &&
        Array.isArray(action.if) &&
        'then' in action &&
        Array.isArray(action.then));
}
/** Returns true if the action is a service or action call */
function isServiceAction(action) {
    return (typeof action === 'object' &&
        action !== null &&
        (typeof action.service === 'string' ||
            typeof action.action === 'string'));
}
/** Returns true if the action is an inline condition (guard) in the action sequence */
function isConditionAction(action) {
    return (typeof action === 'object' &&
        action !== null &&
        'condition' in action &&
        typeof action.condition === 'string');
}
/** Returns true if the action is a variables block */
function isVariablesAction(action) {
    return (typeof action === 'object' &&
        action !== null &&
        'variables' in action &&
        typeof action.variables === 'object' &&
        // Make sure it's not mistaken for other action types that might have variables
        !('service' in action) &&
        !('action' in action) &&
        !('delay' in action) &&
        !('wait_template' in action) &&
        !('choose' in action) &&
        !('if' in action));
}
/** Returns true if the action is a set_conversation_response action */
function isSetConversationResponseAction(action) {
    return typeof action === 'object' && action !== null && 'set_conversation_response' in action;
}
/** Returns true if the action is a stop action */
function isStopAction(action) {
    return typeof action === 'object' && action !== null && 'stop' in action;
}
/** Returns true if the action is a repeat block */
function isRepeatAction(action) {
    return (typeof action === 'object' &&
        action !== null &&
        'repeat' in action &&
        typeof action.repeat === 'object' &&
        action.repeat !== null);
}
/** Returns true if the action is an event firing action */
function isEventAction(action) {
    return (typeof action === 'object' &&
        action !== null &&
        'event' in action &&
        typeof action.event === 'string');
}
/**
 * Valid condition types for Home Assistant
 */
const VALID_CONDITIONS = [
    'state',
    'numeric_state',
    'template',
    'time',
    'sun',
    'zone',
    'and',
    'or',
    'not',
    'device',
    'trigger',
];
/**
 * Transform an array of Home Assistant conditions to internal format
 */
function transformConditions(conditions) {
    return conditions.map((c) => transformToNestedCondition(c));
}
/**
 * Transform Home Assistant condition format to internal nested condition format
 * HA uses 'condition' field, internal schema uses 'condition'
 * Recursively handles nested conditions for and/or/not
 */
function transformToNestedCondition(condition) {
    // Use spread pattern to preserve unknown properties from custom integrations
    const { condition: conditionField, conditions, ...rest } = condition;
    const conditionType = conditionField || 'template';
    const validatedType = VALID_CONDITIONS.includes(conditionType)
        ? conditionType
        : 'template';
    // Recursively transform nested conditions if present
    const nestedConditions = Array.isArray(conditions) ? transformConditions(conditions) : undefined;
    return {
        ...rest, // Preserve extra properties (including weekday, after, before, etc.)
        condition: validatedType,
        conditions: nestedConditions,
    };
}
/**
 * Parser for converting Home Assistant YAML back to FlowGraph
 */
export class YamlParser {
    /**
     * Parse Home Assistant YAML string into FlowGraph
     */
    async parse(yamlString) {
        const warnings = [];
        try {
            // Step 1: Parse YAML string
            let parsed = yamlLoad(yamlString);
            // Handle array format (list of automations) - use the first one
            if (Array.isArray(parsed)) {
                if (parsed.length === 0) {
                    return {
                        success: false,
                        errors: ['Empty automation array'],
                        warnings,
                        hadMetadata: false,
                    };
                }
                parsed = parsed[0];
            }
            if (!parsed || typeof parsed !== 'object') {
                return {
                    success: false,
                    errors: ['Invalid YAML structure'],
                    warnings,
                    hadMetadata: false,
                };
            }
            // Step 2: Extract C.A.F.E. metadata if present
            const metadata = this.extractMetadata(parsed);
            const hadMetadata = metadata !== null;
            // Step 2b: Extract user-defined variables (excluding _cafe_metadata)
            const userVariables = this.extractUserVariables(parsed);
            // Step 3: Only support automation format (no script import)
            const content = parsed;
            // Defensive: ensure content is Record<string, unknown>
            if (typeof content !== 'object' || content === null) {
                return {
                    success: false,
                    errors: ['Invalid YAML content structure'],
                    warnings,
                    hadMetadata,
                };
            }
            // Step 4: Extract node IDs from metadata if available
            const metadataNodeIds = metadata ? Object.keys(metadata.nodes) : [];
            // Step 5: Check if this is a state-machine format automation
            const isStateMachine = metadata?.strategy === 'state-machine' || this.detectStateMachineFormat(content);
            // Step 6: Parse nodes and edges from YAML structure
            const { nodes, edges } = isStateMachine
                ? this.parseStateMachineStructure(content, warnings, metadataNodeIds)
                : this.parseAutomationStructure(content, warnings, metadataNodeIds);
            // Step 7: Apply positions from metadata or generate heuristic layout
            let nodesWithPositions;
            if (hadMetadata && metadata) {
                nodesWithPositions = this.applyMetadataPositions(nodes, metadata);
            }
            else {
                // Use async heuristic layout if metadata is missing
                nodesWithPositions = await applyHeuristicLayout(nodes, edges);
            }
            // Step 8: Build FlowGraph object
            // Validate and parse metadata block using FlowGraphMetadataSchema
            const rawMetadata = {
                mode: content.mode,
                max: content.max,
                max_exceeded: content.max_exceeded,
                initial_state: content.initial_state,
                hide_entity: content.hide_entity,
                trace: content.trace,
            };
            const metadataResult = FlowGraphMetadataSchema.safeParse(rawMetadata);
            const metadataBlock = metadataResult.success
                ? metadataResult.data
                : FlowGraphMetadataSchema.parse({});
            const userTriggerVariables = typeof content.trigger_variables === 'object' &&
                content.trigger_variables !== null &&
                !Array.isArray(content.trigger_variables)
                ? content.trigger_variables
                : undefined;
            const graph = {
                id: metadata?.graph_id || generateGraphId(),
                name: typeof content.alias === 'string' ? content.alias : 'Imported Automation',
                description: typeof content.description === 'string' ? content.description : '',
                nodes: nodesWithPositions,
                edges,
                metadata: metadataBlock,
                version: 1,
                // Preserve user-defined variables for round-trip
                userVariables: Object.keys(userVariables).length > 0 ? userVariables : undefined,
                userTriggerVariables: userTriggerVariables && Object.keys(userTriggerVariables).length > 0
                    ? userTriggerVariables
                    : undefined,
            };
            // Step 7: Validate with Zod schema
            const validation = FlowGraphSchema.safeParse(graph);
            if (!validation.success) {
                // Enhanced error logging: show node data and schema path
                // Zod v4 uses 'issues' instead of 'errors'
                const errorDetails = validation.error.issues.map((e) => {
                    let nodeInfo = '';
                    if (e.path && e.path.length > 0) {
                        // Try to extract node id/type if error is in nodes array
                        if (e.path[0] === 'nodes' && typeof e.path[1] === 'number') {
                            const idx = e.path[1];
                            const node = graph.nodes[idx];
                            nodeInfo = `Node index ${idx} (id: ${node?.id}, type: ${node?.type})\nData: ${JSON.stringify(node?.data, null, 2)}`;
                        }
                    }
                    return `Schema path: ${e.path.join('.')}\nMessage: ${e.message}${nodeInfo ? `\n${nodeInfo}` : ''}`;
                });
                // Also log to console for debugging
                console.error('Zod validation error details:', errorDetails);
                return {
                    success: false,
                    errors: errorDetails,
                    warnings,
                    hadMetadata,
                };
            }
            // Step 8: Validate graph structure (triggers, edges, etc.)
            const structureValidation = validateGraphStructure(validation.data);
            if (!structureValidation.valid) {
                return {
                    success: false,
                    errors: structureValidation.errors,
                    warnings,
                    hadMetadata,
                };
            }
            return {
                success: true,
                graph: validation.data,
                warnings,
                hadMetadata,
            };
        }
        catch (error) {
            // Enhanced catch block: log YAML and error
            console.error('YAML parsing error:', error);
            console.error('YAML string:', yamlString);
            return {
                success: false,
                errors: [error instanceof Error ? error.message : 'Unknown parsing error'],
                warnings,
                hadMetadata: false,
            };
        }
    }
    /**
     * Extract C.A.F.E. metadata from variables section
     */
    /**
     * Extract and validate C.A.F.E. metadata from variables section using Zod schema.
     * Returns CafeMetadata if valid, otherwise null.
     */
    extractMetadata(parsed) {
        try {
            let variables;
            if (typeof parsed.variables === 'object' && parsed.variables !== null) {
                variables = parsed.variables;
            }
            if (variables &&
                typeof variables === 'object' &&
                '_cafe_metadata' in variables &&
                typeof variables._cafe_metadata === 'object' &&
                variables._cafe_metadata !== null) {
                const metadata = variables._cafe_metadata;
                const result = CafeMetadataSchema.safeParse(metadata);
                if (result.success) {
                    return result.data;
                }
            }
        }
        catch {
            // Metadata not present or malformed
        }
        return null;
    }
    /**
     * Extract user-defined variables from the root variables section.
     * Excludes _cafe_metadata which is handled separately.
     */
    extractUserVariables(parsed) {
        const userVariables = {};
        if (typeof parsed.variables === 'object' && parsed.variables !== null) {
            const variables = parsed.variables;
            for (const [key, value] of Object.entries(variables)) {
                // Skip _cafe_metadata - it's handled separately
                if (key !== '_cafe_metadata') {
                    userVariables[key] = value;
                }
            }
        }
        return userVariables;
    }
    /**
     * Detect if automation is in state-machine format
     * State-machine format has:
     * - A variables action with current_node and flow_context
     * - A repeat loop with choose blocks
     */
    detectStateMachineFormat(content) {
        const actions = (content.actions || content.action);
        if (!Array.isArray(actions))
            return false;
        let hasCurrentNodeVar = false;
        let hasRepeatChoose = false;
        for (const action of actions) {
            const actionObj = action;
            // Check for variables with current_node
            if (actionObj.variables) {
                const vars = actionObj.variables;
                if ('current_node' in vars && 'flow_context' in vars) {
                    hasCurrentNodeVar = true;
                }
            }
            // Check for repeat with choose
            if (actionObj.repeat) {
                const repeat = actionObj.repeat;
                const sequence = repeat.sequence;
                if (Array.isArray(sequence)) {
                    for (const seqItem of sequence) {
                        const seqObj = seqItem;
                        if (Array.isArray(seqObj.choose)) {
                            hasRepeatChoose = true;
                            break;
                        }
                    }
                }
            }
        }
        return hasCurrentNodeVar && hasRepeatChoose;
    }
    /**
     * Parse state-machine format automation into nodes and edges
     *
     * State-machine format structure:
     * - Triggers are parsed normally
     * - Actions contain: variables (current_node init) + repeat/choose blocks
     * - Each choose block represents a node:
     *   - condition: {{ current_node == "node-id" }}
     *   - sequence: [node action, variables: { current_node: "next-node" }]
     */
    parseStateMachineStructure(content, warnings, metadataNodeIds) {
        const nodes = [];
        const edges = [];
        // Find the entry node and parse the state machine
        const actions = (content.actions || content.action);
        if (!Array.isArray(actions)) {
            warnings.push('No actions found in automation');
            return { nodes, edges };
        }
        let entryNodeId = null;
        const nodeInfoMap = new Map();
        for (const action of actions) {
            const actionObj = action;
            // Find entry node from initial variables
            if (actionObj.variables) {
                const vars = actionObj.variables;
                if (typeof vars.current_node === 'string' && vars.current_node !== 'END') {
                    entryNodeId = vars.current_node;
                }
            }
            // Parse repeat/choose structure
            if (actionObj.repeat) {
                const repeat = actionObj.repeat;
                const sequence = repeat.sequence;
                if (Array.isArray(sequence)) {
                    for (const seqItem of sequence) {
                        const seqObj = seqItem;
                        if (Array.isArray(seqObj.choose)) {
                            for (const chooseBlock of seqObj.choose) {
                                const nodeInfo = this.parseStateMachineChooseBlock(chooseBlock);
                                if (nodeInfo) {
                                    nodeInfoMap.set(nodeInfo.nodeId, nodeInfo);
                                }
                            }
                        }
                    }
                }
            }
        }
        // Resolve synthetic __parallel_* entries. The transpiler generates these for
        // a trigger with multiple targets (__parallel_trigger_N) and for a condition
        // handle leading to several nodes (__parallel_cond_<id>__<handle>). Expand
        // them back into direct edges instead of leaving phantom nodes behind.
        // Matched strictly: a user node whose ID merely starts with __parallel_ must
        // not be deleted, or every edge pointing at it dangles and the automation
        // stops importing altogether.
        const SYNTHETIC_ENTRY_ID = /^__parallel_(trigger_\d+|cond_.+__(?:true|false))$/;
        const parallelTriggerTargets = new Map();
        for (const [nodeId, info] of nodeInfoMap) {
            if (!SYNTHETIC_ENTRY_ID.test(nodeId))
                continue;
            const targetIds = this.parseInlineParallelBranches(info.parallelItems ?? [], nodeInfoMap);
            if (targetIds.length > 0) {
                parallelTriggerTargets.set(nodeId, targetIds);
            }
            nodeInfoMap.delete(nodeId);
        }
        const parallelFanOutTargets = this.resolveParallelFanOuts(nodeInfoMap);
        // In state-machine strategy, action/condition/delay/wait node IDs are extracted
        // directly from the Jinja2 templates in the YAML choose blocks. Only trigger
        // node IDs need to be allocated via getNextNodeId, so we filter out IDs that
        // are already claimed by the choose blocks to avoid assigning them to triggers.
        const stateMachineNodeIds = new Set(nodeInfoMap.keys());
        const triggerMetadataIds = metadataNodeIds.filter((id) => !stateMachineNodeIds.has(id));
        let triggerIdIndex = 0;
        let nodeIdIndex = 0;
        const getNextNodeId = (type) => {
            if (triggerIdIndex < triggerMetadataIds.length) {
                return triggerMetadataIds[triggerIdIndex++];
            }
            return generateNodeId(type, nodeIdIndex++);
        };
        // Parse triggers
        const triggerData = content.triggers || content.trigger;
        if (!triggerData) {
            warnings.push('No triggers found in automation');
            return { nodes, edges };
        }
        const triggers = Array.isArray(triggerData) ? triggerData : [triggerData];
        const triggerNodes = this.parseTriggers(triggers, warnings, getNextNodeId);
        nodes.push(...triggerNodes);
        // Create nodes from parsed info
        for (const [nodeId, info] of nodeInfoMap) {
            const nodeType = info.nodeType;
            switch (nodeType) {
                case 'condition':
                    nodes.push({
                        id: nodeId,
                        type: 'condition',
                        position: { x: 0, y: 0 },
                        data: info.data,
                    });
                    break;
                case 'action':
                    nodes.push({
                        id: nodeId,
                        type: 'action',
                        position: { x: 0, y: 0 },
                        data: info.data,
                    });
                    break;
                case 'delay':
                    nodes.push({
                        id: nodeId,
                        type: 'delay',
                        position: { x: 0, y: 0 },
                        data: info.data,
                    });
                    break;
                case 'wait':
                    nodes.push({
                        id: nodeId,
                        type: 'wait',
                        position: { x: 0, y: 0 },
                        data: info.data,
                    });
                    break;
                case 'set_variables':
                    nodes.push({
                        id: nodeId,
                        type: 'set_variables',
                        position: { x: 0, y: 0 },
                        data: info.data,
                    });
                    break;
            }
        }
        // Create edges
        // Connect triggers to entry node(s)
        if (entryNodeId) {
            // Check if entryNodeId is a Jinja2 template for trigger routing
            const triggerRouting = this.parseEntryNodeTemplate(entryNodeId);
            if (triggerRouting && triggerRouting.size > 0) {
                // Different triggers route to different nodes
                for (let i = 0; i < triggerNodes.length; i++) {
                    const targetNodeId = triggerRouting.get(i);
                    if (targetNodeId) {
                        // Expand synthetic parallel trigger entries into direct edges
                        const expandedTargets = parallelTriggerTargets.get(targetNodeId);
                        if (expandedTargets) {
                            for (const actualTarget of expandedTargets) {
                                edges.push(this.createEdge(triggerNodes[i].id, actualTarget));
                            }
                        }
                        else {
                            edges.push(this.createEdge(triggerNodes[i].id, targetNodeId));
                        }
                    }
                }
            }
            else {
                // All triggers route to same node (simple case). That node may still be
                // a synthetic __parallel_trigger_* entry — a single trigger fanning out
                // produces a bare id rather than a routing template — so expand it here
                // too, otherwise the edge points at a node that no longer exists.
                const expandedTargets = parallelTriggerTargets.get(entryNodeId);
                for (const trigger of triggerNodes) {
                    if (expandedTargets) {
                        for (const actualTarget of expandedTargets) {
                            edges.push(this.createEdge(trigger.id, actualTarget));
                        }
                    }
                    else {
                        edges.push(this.createEdge(trigger.id, entryNodeId));
                    }
                }
            }
        }
        // Create edges between nodes based on transitions
        for (const [nodeId, info] of nodeInfoMap) {
            edges.push(...this.buildTransitionEdges(nodeId, info, parallelTriggerTargets, parallelFanOutTargets));
        }
        return { nodes, edges };
    }
    /**
     * Build the outgoing edges for one parsed state-machine node.
     *
     * A target may be a synthetic __parallel_* entry, which stands for several
     * branches; those expand into one edge per branch, all keeping the handle of
     * the transition they came from.
     */
    buildTransitionEdges(nodeId, info, syntheticParallelTargets, fanOutTargets) {
        const edges = [];
        const pushTransition = (target, sourceHandle) => {
            for (const actual of syntheticParallelTargets.get(target) ?? [target]) {
                edges.push({
                    id: `edge-${nodeId}-${actual}`,
                    source: nodeId,
                    target: actual,
                    sourceHandle,
                });
            }
        };
        if (info.trueTarget && info.trueTarget !== 'END') {
            const isBranch = info.nodeType === 'condition' || info.falseTarget;
            pushTransition(info.trueTarget, isBranch ? 'true' : undefined);
        }
        if (info.falseTarget && info.falseTarget !== 'END') {
            pushTransition(info.falseTarget, 'false');
        }
        // Fan-out: one edge per inlined parallel branch
        for (const target of fanOutTargets.get(nodeId) ?? []) {
            edges.push(this.createEdge(nodeId, target));
        }
        return edges;
    }
    /**
     * Parse Jinja2 entry node template to extract trigger-to-node routing
     *
     * Template format: {% if trigger.idx == "0" %}action_0{% elif trigger.idx == "1" %}action_1{% else %}action_2{% endif %}
     * Note: trigger.idx is a string in HA, so comparisons use quoted values
     * Returns a Map where key = trigger index, value = target node ID
     */
    parseEntryNodeTemplate(entryNodeId) {
        // Check if it's a Jinja2 template
        if (!entryNodeId.includes('{%') || !entryNodeId.includes('trigger.idx')) {
            return null;
        }
        const routing = new Map();
        // Match {% if trigger.idx == "N" %}nodeId or {% elif trigger.idx == "N" %}nodeId
        // trigger.idx is a string in HA, so index is quoted; node IDs are NOT quoted
        const ifPattern = /{%\s*(?:if|elif)\s+trigger\.idx\s*==\s*["'](\d+)["']\s*%}\s*([^{%]+?)(?={%|$)/g;
        const matches = entryNodeId.matchAll(ifPattern);
        for (const match of matches) {
            const triggerIdx = parseInt(match[1], 10);
            const nodeId = match[2].trim();
            routing.set(triggerIdx, nodeId);
        }
        // Match {% else %}nodeId for the default case (last trigger if not explicitly matched)
        const elseMatch = entryNodeId.match(/{%\s*else\s*%}\s*([^{%]+?)(?={%|$)/);
        if (elseMatch && routing.size > 0) {
            // The else branch is for the last trigger index not explicitly matched
            // Find the highest trigger index and add 1
            const maxIdx = Math.max(...routing.keys());
            routing.set(maxIdx + 1, elseMatch[1].trim());
        }
        return routing.size > 0 ? routing : null;
    }
    /**
     * Resolve parallel fan-out on regular nodes. When a node has more than one
     * outgoing edge the transpiler emits its downstream branches inline inside a
     * parallel block, so expand those back into direct node→target edges.
     *
     * Repeats to a fixpoint because a branch may itself contain a nested fan-out,
     * which only becomes visible once that branch has been parsed.
     */
    resolveParallelFanOuts(nodeInfoMap) {
        const fanOutTargets = new Map();
        const resolved = new Set();
        let foundMore = true;
        while (foundMore) {
            foundMore = false;
            // Snapshot: parseInlineParallelBranches inserts nodes into nodeInfoMap as it goes
            for (const [nodeId, info] of [...nodeInfoMap]) {
                if (!info.parallelItems || info.parallelItems.length === 0)
                    continue;
                if (resolved.has(nodeId))
                    continue;
                resolved.add(nodeId);
                foundMore = true;
                const targetIds = this.parseInlineParallelBranches(info.parallelItems, nodeInfoMap);
                if (targetIds.length > 0) {
                    fanOutTargets.set(nodeId, targetIds);
                }
            }
        }
        return fanOutTargets;
    }
    /**
     * Parse inline parallel branch items into nodes and edges.
     * Reconstructs the subgraph that was inlined by the transpiler's generateInlineBranch.
     * Returns the root node IDs of each branch (for trigger→target edge creation).
     */
    parseInlineParallelBranches(parallelItems, nodeInfoMap) {
        const targetIds = [];
        let idCounter = 0;
        const generateId = (type) => `inline_${type}_${idCounter++}`;
        for (const item of parallelItems) {
            const pItem = item;
            const alias = pItem.alias;
            // New format: { alias: "parallel_branch:<nodeId>", ... }
            const branchMatch = alias?.match(/^parallel_branch:(.+)$/);
            if (branchMatch) {
                const rootNodeId = branchMatch[1];
                targetIds.push(rootNodeId);
                // Parse the branch content into nodes
                if (Array.isArray(pItem.sequence)) {
                    this.parseInlineActionList(pItem.sequence, rootNodeId, nodeInfoMap, generateId);
                }
                else {
                    this.parseInlineActionItem(pItem, rootNodeId, nodeInfoMap, generateId);
                }
                continue;
            }
            // Legacy format: { action: "system_log.write", data: { message: "Node: <nodeId>" } }
            const action = (pItem.service ?? pItem.action);
            if (action === 'system_log.write') {
                const data = pItem.data;
                const message = data?.message;
                if (message) {
                    const nodeMatch = message.match(/^Node:\s*(.+)$/);
                    if (nodeMatch) {
                        targetIds.push(nodeMatch[1]);
                    }
                }
            }
        }
        return targetIds;
    }
    /**
     * Parse a list of inline HA actions, chaining them sequentially.
     * The first action uses firstNodeId; subsequent actions get generated IDs.
     */
    parseInlineActionList(actions, firstNodeId, nodeInfoMap, generateId) {
        let prevNodeId = null;
        for (let i = 0; i < actions.length; i++) {
            const action = actions[i];
            // A bare parallel block is the previous node's fan-out, not a node of its
            // own. Attach it so the fan-out pass can rebuild those edges.
            if (Array.isArray(action.parallel) && action.alias === undefined) {
                const prevInfo = prevNodeId ? nodeInfoMap.get(prevNodeId) : undefined;
                if (prevInfo) {
                    prevInfo.parallelItems = action.parallel;
                }
                continue;
            }
            const { nodeId: embeddedId } = this.extractCafeNodeId(action.alias);
            const nodeId = i === 0 ? firstNodeId : (embeddedId ?? generateId(this.inferInlineNodeType(action)));
            // Chain previous non-condition node to this one
            if (prevNodeId) {
                const prevInfo = nodeInfoMap.get(prevNodeId);
                if (prevInfo && prevInfo.nodeType !== 'condition') {
                    prevInfo.trueTarget = nodeId;
                }
            }
            this.parseInlineActionItem(action, nodeId, nodeInfoMap, generateId);
            prevNodeId = nodeId;
        }
    }
    /**
     * Parse a single inline HA action item into a StateMachineNodeInfo entry.
     * Handles actions, conditions (if/then/else), delays, and waits.
     */
    parseInlineActionItem(item, nodeId, nodeInfoMap, generateId) {
        // Strip parallel_branch: prefix from alias if present
        const rawAlias = item.alias;
        const { cleanAlias: cafeStripped } = this.extractCafeNodeId(rawAlias);
        const alias = cafeStripped?.startsWith('parallel_branch:') ? undefined : cafeStripped;
        if (item.if && Array.isArray(item.if)) {
            // Condition node (if/then/else)
            const data = this.conditionDataFromIfBlock(item, alias);
            let trueTarget = null;
            let falseTarget = null;
            const thenActions = item.then;
            if (thenActions && thenActions.length > 0) {
                const { nodeId: thenEmbeddedId } = this.extractCafeNodeId(thenActions[0].alias);
                const thenNodeId = thenEmbeddedId ?? generateId(this.inferInlineNodeType(thenActions[0]));
                trueTarget = thenNodeId;
                this.parseInlineActionList(thenActions, thenNodeId, nodeInfoMap, generateId);
            }
            const elseActions = item.else;
            if (elseActions && elseActions.length > 0) {
                const { nodeId: elseEmbeddedId } = this.extractCafeNodeId(elseActions[0].alias);
                const elseNodeId = elseEmbeddedId ?? generateId(this.inferInlineNodeType(elseActions[0]));
                falseTarget = elseNodeId;
                this.parseInlineActionList(elseActions, elseNodeId, nodeInfoMap, generateId);
            }
            nodeInfoMap.set(nodeId, { nodeId, nodeType: 'condition', data, trueTarget, falseTarget });
        }
        else if (item.service || item.action) {
            // Action node
            const data = {};
            data.service = (item.service ?? item.action);
            if (item.target)
                data.target = item.target;
            if (item.data)
                data.data = item.data;
            if (alias)
                data.alias = alias;
            nodeInfoMap.set(nodeId, {
                nodeId,
                nodeType: 'action',
                data,
                trueTarget: null,
                falseTarget: null,
            });
        }
        else if (item.delay !== undefined) {
            // Delay node
            const data = { delay: item.delay };
            if (alias)
                data.alias = alias;
            nodeInfoMap.set(nodeId, {
                nodeId,
                nodeType: 'delay',
                data,
                trueTarget: null,
                falseTarget: null,
            });
        }
        else if (item.wait_template !== undefined || item.wait_for_trigger !== undefined) {
            // Wait node
            const data = {};
            if (item.wait_template)
                data.wait_template = item.wait_template;
            if (item.wait_for_trigger)
                data.wait_for_trigger = item.wait_for_trigger;
            if (item.timeout)
                data.timeout = item.timeout;
            if (item.continue_on_timeout !== undefined)
                data.continue_on_timeout = item.continue_on_timeout;
            if (alias)
                data.alias = alias;
            nodeInfoMap.set(nodeId, {
                nodeId,
                nodeType: 'wait',
                data,
                trueTarget: null,
                falseTarget: null,
            });
        }
        else if (item.variables !== undefined) {
            // Set variables node
            const data = { variables: item.variables };
            if (alias)
                data.alias = alias;
            if (item.id)
                data.id = item.id;
            nodeInfoMap.set(nodeId, {
                nodeId,
                nodeType: 'set_variables',
                data,
                trueTarget: null,
                falseTarget: null,
            });
        }
    }
    /**
     * Extract a C.A.F.E. node ID encoded in an alias field.
     * Handles format: "cafe_node:<nodeId>" or "cafe_node:<nodeId>:<userAlias>"
     */
    extractCafeNodeId(alias) {
        if (!alias)
            return { nodeId: null, cleanAlias: undefined };
        const match = alias.match(/^cafe_node:([^:]+)(?::(.+))?$/);
        if (match) {
            return { nodeId: match[1], cleanAlias: match[2] || undefined };
        }
        return { nodeId: null, cleanAlias: alias };
    }
    /**
     * Infer the node type from an inline HA action item.
     */
    inferInlineNodeType(item) {
        if (item.if)
            return 'condition';
        if (item.delay !== undefined)
            return 'delay';
        if (item.wait_template !== undefined || item.wait_for_trigger !== undefined)
            return 'wait';
        if (item.variables !== undefined)
            return 'set_variables';
        return 'action';
    }
    /**
     * Parse a single choose block from state-machine format
     */
    parseStateMachineChooseBlock(chooseBlock) {
        const conditions = chooseBlock.conditions;
        if (!Array.isArray(conditions) || conditions.length === 0) {
            return null;
        }
        // Extract node ID from condition: {{ current_node == "node-id" }}
        const firstCondition = conditions[0];
        const valueTemplate = firstCondition.value_template;
        if (!valueTemplate)
            return null;
        const match = valueTemplate.match(/current_node\s*==\s*["']([^"']+)["']/);
        if (!match)
            return null;
        const nodeId = match[1];
        const sequence = chooseBlock.sequence;
        if (!Array.isArray(sequence) || sequence.length === 0) {
            return null;
        }
        // Parse sequence to determine node type and data
        let nodeType = 'action';
        const data = {};
        let trueTarget = null;
        let falseTarget = null;
        let parallelItems;
        for (const item of sequence) {
            const seqItem = item;
            // Check for variables action. Two distinct shapes share this key:
            // - the state-machine transition, which carries only `current_node`
            //   (and sometimes `flow_context`)
            // - a user set_variables node, which carries arbitrary user variables
            //
            // Requiring the transition to hold nothing else means a user variable that
            // merely happens to be named `current_node` alongside others is still read
            // back as a set_variables node.
            if (seqItem.variables) {
                const vars = seqItem.variables;
                const isTransition = 'current_node' in vars &&
                    Object.keys(vars).every((key) => key === 'current_node' || key === 'flow_context');
                if (!isTransition) {
                    // User-defined variables: this block represents a set_variables node
                    nodeType = 'set_variables';
                    data.variables = vars;
                    if (seqItem.alias)
                        data.alias = seqItem.alias;
                    if (seqItem.id)
                        data.id = seqItem.id;
                    continue;
                }
                const currentNodeValue = vars.current_node;
                if (typeof currentNodeValue === 'string') {
                    // Check if it's a Jinja conditional (condition node)
                    if (currentNodeValue.includes('{%') && currentNodeValue.includes('%}')) {
                        nodeType = 'condition';
                        // Extract true and false targets
                        const trueMatch = currentNodeValue.match(/{%\s*if[^%]*%}\s*"?([^"'{%]+?)"?(?=\s*{%)/);
                        const falseMatch = currentNodeValue.match(/{%\s*else\s*%}\s*"?([^"'{%]+?)"?(?=\s*{%)/);
                        trueTarget = trueMatch ? trueMatch[1] : null;
                        falseTarget = falseMatch ? falseMatch[1] : null;
                        // Extract condition expression from Jinja template
                        const conditionMatch = currentNodeValue.match(/{%\s*if\s+(.+?)\s*%}/);
                        if (conditionMatch) {
                            const conditionExpr = conditionMatch[1];
                            Object.assign(data, this.parseJinjaCondition(conditionExpr));
                        }
                    }
                    else {
                        // Simple transition
                        trueTarget = currentNodeValue === 'END' ? null : currentNodeValue;
                    }
                }
            }
            // A condition whose template can't be inlined into `{% if %}` is written as
            // a real if/then/else block, each branch carrying that side's transition.
            else if (Array.isArray(seqItem.if)) {
                nodeType = 'condition';
                const { cleanAlias } = this.extractCafeNodeId(typeof seqItem.alias === 'string' ? seqItem.alias : undefined);
                Object.assign(data, this.conditionDataFromIfBlock(seqItem, cleanAlias));
                trueTarget = this.parseTransitionTarget(seqItem.then);
                falseTarget = this.parseTransitionTarget(seqItem.else);
            }
            // Check for delay action
            else if (seqItem.delay !== undefined) {
                nodeType = 'delay';
                data.delay = seqItem.delay;
                if (seqItem.alias)
                    data.alias = seqItem.alias;
            }
            // Check for wait action
            else if (seqItem.wait_template !== undefined) {
                nodeType = 'wait';
                data.wait_template = seqItem.wait_template;
                if (seqItem.timeout)
                    data.timeout = seqItem.timeout;
                if (seqItem.continue_on_timeout !== undefined) {
                    data.continue_on_timeout = seqItem.continue_on_timeout;
                }
                if (seqItem.alias)
                    data.alias = seqItem.alias;
            }
            // Check for parallel block (synthetic __parallel_trigger_* entries)
            else if (Array.isArray(seqItem.parallel)) {
                parallelItems = seqItem.parallel;
            }
            // Check for service call action
            else if (seqItem.service || seqItem.action) {
                nodeType = 'action';
                data.service = seqItem.service || seqItem.action;
                if (seqItem.target)
                    data.target = seqItem.target;
                if (seqItem.data)
                    data.data = seqItem.data;
                if (seqItem.alias)
                    data.alias = seqItem.alias;
            }
        }
        return { nodeId, nodeType, data, trueTarget, falseTarget, parallelItems };
    }
    /**
     * The condition an `if:` block tests, with the block's alias when it carries one.
     * Shared by the two places an `if:` block is read back: a dispatcher entry in the
     * state machine, and an inline branch.
     */
    conditionDataFromIfBlock(item, alias) {
        const conditions = Array.isArray(item.if) ? item.if : [];
        const data = { ...(conditions[0] ?? {}) };
        if (alias)
            data.alias = alias;
        return data;
    }
    /**
     * The node a state-machine branch hands control to, from its
     * `variables: {current_node: ...}` entry. `END` means the flow stops there.
     */
    parseTransitionTarget(branch) {
        if (!Array.isArray(branch)) {
            return null;
        }
        for (const entry of branch) {
            const variables = entry?.variables;
            const target = variables?.current_node;
            if (typeof target === 'string') {
                return target === 'END' ? null : target;
            }
        }
        return null;
    }
    /**
     * Parse Jinja condition expression to extract condition data.
     * Recognizes top-level `and`/`or`/`not` groupings before attempting to match a
     * single leaf condition, so a compound expression (e.g. an OR node with several
     * conditions) doesn't get collapsed into just the first leaf that happens to
     * match somewhere inside it (see #209).
     */
    parseJinjaCondition(expr) {
        const trimmed = this.stripOuterParens(expr.trim());
        // `or` has the lowest precedence, so split on it first: each side may still
        // contain `and`-joined sub-expressions, which the recursive call handles.
        const orParts = this.splitTopLevelJinja(trimmed, ' or ');
        if (orParts.length > 1) {
            return {
                condition: 'or',
                conditions: orParts.map((part) => this.parseJinjaCondition(part)),
            };
        }
        const andParts = this.splitTopLevelJinja(trimmed, ' and ');
        if (andParts.length > 1) {
            return {
                condition: 'and',
                conditions: andParts.map((part) => this.parseJinjaCondition(part)),
            };
        }
        const notMatch = trimmed.match(/^not\s*\((.*)\)$/s);
        if (notMatch) {
            const innerParts = this.splitTopLevelJinja(notMatch[1].trim(), ' and ');
            return {
                condition: 'not',
                conditions: innerParts.map((part) => this.parseJinjaCondition(part)),
            };
        }
        // is_state('entity', 'state')
        const isStateMatch = trimmed.match(/^is_state\s*\(\s*['"]([^'"]+)['"]\s*,\s*['"]([^'"]+)['"]\s*\)$/);
        if (isStateMatch) {
            const entityId = isStateMatch[1];
            const state = isStateMatch[2];
            // Check for sun entity
            if (entityId === 'sun.sun') {
                if (state === 'above_horizon') {
                    return { condition: 'sun', after: 'sunrise', before: 'sunset' };
                }
                else if (state === 'below_horizon') {
                    return { condition: 'sun', after: 'sunset', before: 'sunrise' };
                }
            }
            return { condition: 'state', entity_id: entityId, state };
        }
        // state_attr('entity', 'attribute') == 'value'
        const stateAttrMatch = trimmed.match(/^state_attr\s*\(\s*['"]([^'"]+)['"]\s*,\s*['"]([^'"]+)['"]\s*\)\s*==\s*['"]([^'"]*)['"]$/);
        if (stateAttrMatch) {
            return {
                condition: 'state',
                entity_id: stateAttrMatch[1],
                attribute: stateAttrMatch[2],
                state: stateAttrMatch[3],
            };
        }
        // states('entity') | float > number
        const numericMatch = trimmed.match(/^states\s*\(\s*['"]([^'"]+)['"]\s*\)\s*\|\s*float\s*([<>=]+)\s*(\d+(?:\.\d+)?)$/);
        if (numericMatch) {
            const entityId = numericMatch[1];
            const operator = numericMatch[2];
            const value = parseFloat(numericMatch[3]);
            const result = {
                condition: 'numeric_state',
                entity_id: entityId,
            };
            if (operator.includes('>'))
                result.above = value;
            if (operator.includes('<'))
                result.below = value;
            return result;
        }
        // Fallback to template condition
        return { condition: 'template', value_template: `{{ ${trimmed} }}` };
    }
    /**
     * Remove a single redundant pair of wrapping parentheses, e.g. `(a or b)` -> `a or b`.
     * Only strips when the opening paren's matching close is the expression's final
     * character - `(a) or (b)` is left untouched.
     */
    stripOuterParens(expr) {
        let result = expr.trim();
        while (result.startsWith('(') && result.endsWith(')')) {
            let depth = 0;
            let matchesToEnd = true;
            for (let i = 0; i < result.length; i++) {
                if (result[i] === '(')
                    depth++;
                else if (result[i] === ')') {
                    depth--;
                    if (depth === 0 && i !== result.length - 1) {
                        matchesToEnd = false;
                        break;
                    }
                }
            }
            if (!matchesToEnd)
                break;
            result = result.slice(1, -1).trim();
        }
        return result;
    }
    /**
     * Split a Jinja expression on a logical operator (` and `/` or `), ignoring
     * occurrences inside parentheses or quoted string literals.
     */
    splitTopLevelJinja(expr, separator) {
        const parts = [];
        let depth = 0;
        let quote = null;
        let current = '';
        let i = 0;
        while (i < expr.length) {
            const ch = expr[i];
            if (quote) {
                current += ch;
                if (ch === quote && expr[i - 1] !== '\\')
                    quote = null;
                i++;
                continue;
            }
            if (ch === "'" || ch === '"') {
                quote = ch;
                current += ch;
                i++;
                continue;
            }
            if (ch === '(') {
                depth++;
                current += ch;
                i++;
                continue;
            }
            if (ch === ')') {
                depth--;
                current += ch;
                i++;
                continue;
            }
            if (depth === 0 && expr.slice(i, i + separator.length) === separator) {
                parts.push(current.trim());
                current = '';
                i += separator.length;
                continue;
            }
            current += ch;
            i++;
        }
        parts.push(current.trim());
        return parts;
    }
    /**
     * Parse automation structure into nodes and edges (native format)
     */
    parseAutomationStructure(content, warnings, metadataNodeIds) {
        const nodes = [];
        const edges = [];
        const conditionNodeIds = new Set();
        // Group metadata IDs by node type for type-aware assignment.
        // Without type-aware grouping, depth-first parsing of parallel branches
        // would assign IDs in the wrong order (e.g., an action gets a condition's ID).
        const knownNodeTypes = ['set_variables', 'trigger', 'condition', 'action', 'delay', 'wait'];
        const metadataIdsByType = new Map();
        const usedMetadataIds = new Set();
        for (const id of metadataNodeIds) {
            const matchedType = knownNodeTypes.find((t) => id.startsWith(`${t}_`));
            if (matchedType) {
                if (!metadataIdsByType.has(matchedType))
                    metadataIdsByType.set(matchedType, []);
                metadataIdsByType.get(matchedType).push(id);
            }
        }
        const metadataTypeIndexes = new Map();
        let sequentialFallbackIndex = 0;
        let nodeIdCounter = metadataNodeIds.length;
        // Helper to get next node ID (from metadata if available, otherwise generate)
        const getNextNodeId = (type) => {
            // First: try type-matched metadata ID
            const ids = metadataIdsByType.get(type);
            const idx = metadataTypeIndexes.get(type) ?? 0;
            if (ids && idx < ids.length) {
                const id = ids[idx];
                metadataTypeIndexes.set(type, idx + 1);
                usedMetadataIds.add(id);
                return id;
            }
            // Second: fallback to next unused metadata ID (handles non-standard ID formats)
            while (sequentialFallbackIndex < metadataNodeIds.length) {
                const id = metadataNodeIds[sequentialFallbackIndex++];
                if (!usedMetadataIds.has(id)) {
                    usedMetadataIds.add(id);
                    return id;
                }
            }
            // Third: generate a new ID
            return generateNodeId(type, nodeIdCounter++);
        };
        // Parse triggers (support both 'trigger' and 'triggers')
        const triggerData = content.triggers || content.trigger;
        if (!triggerData) {
            warnings.push('No triggers found in automation');
            return { nodes, edges };
        }
        const triggers = Array.isArray(triggerData) ? triggerData : [triggerData];
        const triggerNodes = this.parseTriggers(triggers, warnings, getNextNodeId);
        nodes.push(...triggerNodes);
        // Build a map from trigger node ID → trigger's `id` field (for trigger-id condition routing)
        const triggerNodeMap = new Map();
        for (let i = 0; i < triggerNodes.length; i++) {
            const triggerId = triggers[i]?.id;
            if (typeof triggerId === 'string') {
                triggerNodeMap.set(triggerNodes[i].id, triggerId);
            }
        }
        // Parse conditions (if present at top level - support both 'condition' and 'conditions')
        let firstActionNodeIds = [];
        const conditionData = content.conditions || content.condition;
        // Normalize to array and check if non-empty
        const conditions = Array.isArray(conditionData)
            ? conditionData
            : conditionData
                ? [conditionData]
                : [];
        if (conditions.length > 0) {
            const conditionResults = this.parseConditions(conditions, warnings, getNextNodeId);
            nodes.push(...conditionResults.nodes);
            edges.push(...conditionResults.edges);
            // Track condition node IDs
            for (const condNode of conditionResults.nodes) {
                conditionNodeIds.add(condNode.id);
            }
            // Root-level conditions in Home Assistant are implicitly AND-ed together.
            // They should be chained sequentially: trigger → cond1 → cond2 → cond3 → actions
            // Each condition's TRUE path leads to the next condition (or to actions if last)
            const conditionNodes = conditionResults.nodes;
            if (conditionNodes.length === 1) {
                // Single condition - connect triggers to it
                for (const trigger of triggerNodes) {
                    edges.push(this.createEdge(trigger.id, conditionNodes[0].id));
                }
                firstActionNodeIds = [conditionNodes[0].id];
            }
            else {
                // Multiple conditions - chain them sequentially
                // Connect triggers to first condition
                for (const trigger of triggerNodes) {
                    edges.push(this.createEdge(trigger.id, conditionNodes[0].id));
                }
                // Chain conditions: each condition's TRUE path leads to next condition
                for (let i = 0; i < conditionNodes.length - 1; i++) {
                    edges.push(this.createEdge(conditionNodes[i].id, conditionNodes[i + 1].id, 'true'));
                }
                // The last condition's TRUE path leads to actions
                firstActionNodeIds = [conditionNodes[conditionNodes.length - 1].id];
            }
        }
        else {
            firstActionNodeIds = triggerNodes.map((t) => t.id);
        }
        // Parse actions (support both 'action' and 'actions')
        const actionData = content.actions || content.action;
        if (!actionData) {
            warnings.push('No actions found in automation');
            return { nodes, edges };
        }
        const actions = Array.isArray(actionData) ? actionData : [actionData];
        const actionResults = this.parseActions(actions, {
            warnings,
            previousNodeIds: firstActionNodeIds,
            getNextNodeId,
            conditionNodeIds,
            triggerNodeMap,
        });
        nodes.push(...actionResults.nodes);
        edges.push(...actionResults.edges);
        return { nodes, edges };
    }
    /**
     * Parse trigger configurations
     */
    parseTriggers(triggers, warnings, getNextNodeId) {
        // Process all object-type trigger items — do NOT filter with isHATrigger here,
        // because modern HA may use formats (e.g. dict-keyed or novel trigger types)
        // that don't have 'platform', 'trigger', or 'entity_id' at the top level.
        return triggers
            .filter((t) => typeof t === 'object' && t !== null)
            .map((trigger, index) => {
            const nodeId = getNextNodeId('trigger');
            try {
                // Validate and parse trigger using HATriggerSchema
                const result = HATriggerSchema.safeParse(trigger);
                if (!result.success) {
                    warnings.push(`Trigger ${index} failed schema validation: ${JSON.stringify(result.error.issues)}`);
                    // Return a fallback TRIGGER node (not an action node) so the graph
                    // always has at least one trigger — allowing the import to succeed.
                    return this.createFallbackTriggerNode(nodeId, trigger);
                }
                // Use platform directly from validated schema
                const node = {
                    id: nodeId,
                    type: 'trigger',
                    position: { x: 0, y: 0 },
                    data: result.data,
                };
                return node;
            }
            catch (error) {
                warnings.push(`Failed to parse trigger ${index}: ${error}`);
                return this.createFallbackTriggerNode(nodeId, trigger);
            }
        });
    }
    /**
     * Build a best-effort trigger node when HATriggerSchema validation fails.
     * Always returns type:'trigger' so validateGraphStructure does not fail.
     */
    createFallbackTriggerNode(nodeId, originalData) {
        const data = typeof originalData === 'object' && originalData !== null
            ? originalData
            : {};
        // Determine trigger type from various formats:
        // 1. Modern HA: { trigger: 'state', ... }
        // 2. Legacy HA: { platform: 'state', ... }
        // 3. Dict-keyed: { state: { entity_id: '...' } }
        let triggerType;
        let nestedFields = {};
        if (typeof data.trigger === 'string') {
            triggerType = data.trigger;
            const { platform: _p, trigger: _t, ...rest } = data;
            nestedFields = rest;
        }
        else if (typeof data.platform === 'string') {
            triggerType = data.platform;
            const { platform: _p, ...rest } = data;
            nestedFields = rest;
        }
        else {
            // Dict-keyed format: first key is the trigger type, value contains fields
            const firstKey = Object.keys(data).find((k) => !['alias', 'id', 'enabled', 'variables'].includes(k));
            if (firstKey && typeof data[firstKey] === 'object' && data[firstKey] !== null) {
                triggerType = firstKey;
                nestedFields = data[firstKey];
            }
            else {
                triggerType = 'state';
            }
        }
        return {
            id: nodeId,
            type: 'trigger',
            position: { x: 0, y: 0 },
            data: {
                trigger: triggerType,
                ...nestedFields,
            },
        };
    }
    /**
     * Parse condition configurations
     */
    parseConditions(conditions, warnings, getNextNodeId) {
        const nodes = [];
        const edges = [];
        const outputNodeIds = [];
        conditions.filter(isHACondition).forEach((condition, index) => {
            const nodeId = getNextNodeId('condition');
            try {
                const result = HAConditionSchema.safeParse(condition);
                if (!result.success) {
                    warnings.push(`Condition ${index} failed schema validation: ${JSON.stringify(result.error.issues)}`);
                    nodes.push({
                        id: nodeId,
                        type: 'condition',
                        position: { x: 0, y: 0 },
                        data: {
                            condition: 'template',
                            alias: 'Unknown Condition',
                            value_template: JSON.stringify(condition),
                        },
                    });
                    return;
                }
                const node = {
                    id: nodeId,
                    type: 'condition',
                    position: { x: 0, y: 0 },
                    data: result.data,
                };
                nodes.push(node);
                outputNodeIds.push(nodeId);
            }
            catch (error) {
                warnings.push(`Failed to parse condition ${index}: ${error}`);
                // Create a minimal valid unknown condition node
                nodes.push({
                    id: nodeId,
                    type: 'condition',
                    position: { x: 0, y: 0 },
                    data: {
                        condition: 'template',
                        alias: 'Unknown Condition',
                        value_template: JSON.stringify(condition),
                    },
                });
            }
        });
        return { nodes, edges, outputNodeIds };
    }
    /**
     * Parse action sequences (including choose blocks, delays, etc.)
     */
    parseActions(actions, options) {
        const { warnings, previousNodeIds, getNextNodeId, conditionNodeIds = new Set(), triggerNodeMap, inheritedEnabled, } = options;
        const nodes = [];
        const edges = [];
        let currentNodeIds = previousNodeIds;
        // Create a mutable copy so we can track condition nodes created during parsing
        const localConditionNodeIds = new Set(conditionNodeIds);
        // Track condition nodes whose FALSE path should connect to next action
        const falsePathConditionIds = new Set();
        // Helper to compute the enabled state for a node
        const getNodeEnabled = (nodeEnabled) => {
            // If parent is disabled, child is always disabled
            if (inheritedEnabled === false)
                return false;
            // Otherwise use the node's own enabled state
            return nodeEnabled;
        };
        // Helper to create edges from current nodes to a target
        const createEdgesFromCurrent = (targetId) => {
            for (const prevId of currentNodeIds) {
                let sourceHandle;
                if (falsePathConditionIds.has(prevId)) {
                    // This condition's FALSE path should connect to next action
                    sourceHandle = 'false';
                }
                else if (localConditionNodeIds.has(prevId)) {
                    // This condition's TRUE path should connect to next action
                    sourceHandle = 'true';
                }
                edges.push(this.createEdge(prevId, targetId, sourceHandle));
            }
        };
        // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: large dispatch switch, refactoring deferred
        actions.forEach((action, index) => {
            if (!action || typeof action !== 'object') {
                // Unknown action type - create unknown node
                warnings.push(`Unknown action type (${JSON.stringify(action)}) at index ${index}`);
                const nodeId = getNextNodeId('unknown');
                nodes.push({
                    id: nodeId,
                    type: 'action',
                    position: { x: 0, y: 0 },
                    data: {
                        alias: 'Unknown Node',
                        service: 'unknown.unknown',
                        data: action,
                    },
                });
                createEdgesFromCurrent(nodeId);
                currentNodeIds = [nodeId];
                return;
            }
            // Handle different action types
            if (isConditionAction(action)) {
                // Inline condition guard in action sequence
                const nodeId = getNextNodeId('condition');
                const act = action;
                const conditionType = act.condition || 'template';
                const validatedType = VALID_CONDITIONS.includes(conditionType)
                    ? conditionType
                    : 'template';
                // Use Zod schema for parsing and type safety
                let parsedData;
                try {
                    parsedData = HAConditionSchema.parse(act);
                }
                catch (e) {
                    warnings.push(`Inline condition at index ${index} failed schema validation: ${e instanceof Error ? e.message : JSON.stringify(e)}`);
                    parsedData = {
                        condition: validatedType,
                        alias: typeof act.alias === 'string' ? act.alias : undefined,
                        value_template: JSON.stringify(act),
                    };
                }
                // Apply inherited enabled state
                parsedData.enabled = getNodeEnabled(parsedData.enabled);
                const conditionNode = {
                    id: nodeId,
                    type: 'condition',
                    position: { x: 0, y: 0 },
                    // It guards the sequence; it is not one of the automation's root conditions.
                    data: markConditionInSequence(parsedData),
                };
                nodes.push(conditionNode);
                createEdgesFromCurrent(nodeId);
                // Track this condition node so subsequent edges use 'true' handle
                localConditionNodeIds.add(nodeId);
                currentNodeIds = [nodeId];
            }
            else if (isVariablesAction(action)) {
                // Variables block - create set_variables node
                const nodeId = getNextNodeId('set_variables');
                const act = action;
                const setVariablesNode = {
                    id: nodeId,
                    type: 'set_variables',
                    position: { x: 0, y: 0 },
                    data: {
                        alias: typeof act.alias === 'string' ? act.alias : undefined,
                        variables: act.variables || {},
                        enabled: getNodeEnabled(typeof act.enabled === 'boolean' ? act.enabled : undefined),
                    },
                };
                nodes.push(setVariablesNode);
                createEdgesFromCurrent(nodeId);
                currentNodeIds = [nodeId];
            }
            else if (isDelayAction(action)) {
                const nodeId = getNextNodeId('delay');
                const act = action;
                // Use spread pattern to preserve unknown properties from custom integrations
                const { alias, delay: delayValue, enabled, ...extraProps } = act;
                const delayNode = {
                    id: nodeId,
                    type: 'delay',
                    position: { x: 0, y: 0 },
                    data: {
                        ...extraProps, // Preserve extra properties
                        alias: typeof alias === 'string' ? alias : undefined,
                        delay: normalizeDelayValue(delayValue),
                        enabled: getNodeEnabled(typeof enabled === 'boolean' ? enabled : undefined),
                    },
                };
                nodes.push(delayNode);
                createEdgesFromCurrent(nodeId);
                currentNodeIds = [nodeId];
            }
            else if (isWaitAction(action)) {
                const nodeId = getNextNodeId('wait');
                const act = action;
                // Use spread pattern to preserve unknown properties from custom integrations
                const { alias, wait_template: waitTemplate, wait_for_trigger: waitForTrigger, timeout: timeoutValue, continue_on_timeout: continueOnTimeoutValue, enabled, ...extraProps } = act;
                // Handle timeout as either string or object format
                let timeout;
                if (typeof timeoutValue === 'string') {
                    timeout = timeoutValue;
                }
                else if (typeof timeoutValue === 'object' && timeoutValue !== null) {
                    timeout = timeoutValue;
                }
                const waitData = {
                    ...extraProps, // Preserve extra properties
                    alias: typeof alias === 'string' ? alias : undefined,
                    timeout,
                    continue_on_timeout: typeof continueOnTimeoutValue === 'boolean' ? continueOnTimeoutValue : undefined,
                    enabled: getNodeEnabled(typeof enabled === 'boolean' ? enabled : undefined),
                };
                if (typeof waitTemplate === 'string') {
                    waitData.wait_template = waitTemplate;
                }
                else if (Array.isArray(waitForTrigger)) {
                    const parsedTriggers = [];
                    for (const trigger of waitForTrigger) {
                        const result = HATriggerSchema.safeParse(trigger);
                        if (result.success) {
                            parsedTriggers.push(result.data);
                        }
                        else {
                            warnings.push(`Failed to parse a trigger inside wait_for_trigger: ${result.error.message}`);
                        }
                    }
                    waitData.wait_for_trigger = parsedTriggers;
                }
                const waitNode = {
                    id: nodeId,
                    type: 'wait',
                    position: { x: 0, y: 0 },
                    data: waitData,
                };
                nodes.push(waitNode);
                createEdgesFromCurrent(nodeId);
                currentNodeIds = [nodeId];
            }
            else if (isChooseAction(action)) {
                // Handle condition branching (choose blocks)
                const chooseResult = this.parseChooseBlock(action, {
                    warnings,
                    previousNodeIds: currentNodeIds,
                    getNextNodeId,
                    conditionNodeIds: localConditionNodeIds,
                    falsePathConditionIds,
                    inheritedEnabled,
                });
                nodes.push(...chooseResult.nodes);
                edges.push(...chooseResult.edges);
                // Add any new condition nodes to our tracking set
                // But NOT condition nodes that are outputs via FALSE path (no default choose)
                for (const outId of chooseResult.outputNodeIds) {
                    const outNode = chooseResult.nodes.find((n) => n.id === outId);
                    if (outNode?.type === 'condition') {
                        if (chooseResult.falsePathOutputIds.includes(outId)) {
                            // This condition's FALSE path should connect to subsequent actions
                            falsePathConditionIds.add(outId);
                        }
                        else {
                            // This condition's TRUE path should connect to subsequent actions
                            localConditionNodeIds.add(outId);
                        }
                    }
                }
                currentNodeIds = chooseResult.outputNodeIds;
            }
            else if (isIfThenAction(action)) {
                // Handle if/then/else blocks
                const act = action;
                const ifArr = Array.isArray(act.if) ? act.if : [];
                const thenArr = Array.isArray(act.then) ? act.then : [];
                const elseArr = Array.isArray(act.else) ? act.else : undefined;
                const ifAction = {
                    if: ifArr,
                    then: thenArr,
                    else: elseArr,
                    alias: typeof act.alias === 'string' ? act.alias : undefined,
                    enabled: act.enabled,
                };
                const ifResult = this.parseIfBlock(ifAction, {
                    warnings,
                    previousNodeIds: currentNodeIds,
                    getNextNodeId,
                    conditionNodeIds: localConditionNodeIds,
                    falsePathConditionIds,
                    triggerNodeMap,
                    inheritedEnabled,
                });
                nodes.push(...ifResult.nodes);
                edges.push(...ifResult.edges);
                // Route condition outputs to the correct handle tracking set
                for (const outId of ifResult.outputNodeIds) {
                    const outNode = ifResult.nodes.find((n) => n.id === outId);
                    if (outNode?.type === 'condition') {
                        if (ifResult.falsePathOutputIds.includes(outId)) {
                            // This condition's FALSE path should connect to subsequent actions
                            falsePathConditionIds.add(outId);
                        }
                        else {
                            // This condition's TRUE path should connect to subsequent actions
                            localConditionNodeIds.add(outId);
                        }
                    }
                }
                // For trigger-id routing: merge unconsumed trigger nodes (those that didn't match
                // this if block's trigger id) back into currentNodeIds so they are available
                // as entry points for the next if block.
                if (ifResult.unconsumedPreviousIds.length > 0) {
                    currentNodeIds = ifResult.unconsumedPreviousIds;
                }
                else {
                    currentNodeIds = ifResult.outputNodeIds;
                }
            }
            else if (isDeviceAction(action)) {
                // Device action (type + device_id + domain)
                const nodeId = getNextNodeId('action');
                const act = action;
                // Extract known metadata fields vs additional parameters
                const knownFields = [
                    'type',
                    'device_id',
                    'domain',
                    'entity_id',
                    'subtype',
                    'alias',
                    'enabled',
                ];
                const additionalParams = {};
                for (const [key, value] of Object.entries(act)) {
                    if (!knownFields.includes(key) && value !== undefined) {
                        additionalParams[key] = value;
                    }
                }
                // Convert device action to service-like format for the action node
                const actionNode = {
                    id: nodeId,
                    type: 'action',
                    position: { x: 0, y: 0 },
                    data: {
                        alias: typeof act.alias === 'string' ? act.alias : undefined,
                        // Store the device action fields directly
                        service: `${act.domain}.${act.type}`,
                        target: {
                            device_id: act.device_id,
                        },
                        // Preserve original device action metadata and additional params (like 'option')
                        data: {
                            type: act.type,
                            device_id: act.device_id,
                            domain: act.domain,
                            entity_id: act.entity_id,
                            subtype: act.subtype,
                            ...additionalParams,
                        },
                        enabled: getNodeEnabled(typeof act.enabled === 'boolean' ? act.enabled : undefined),
                    },
                };
                nodes.push(actionNode);
                createEdgesFromCurrent(nodeId);
                currentNodeIds = [nodeId];
            }
            else if (isParallelAction(action)) {
                // Parallel block - all branches start from the same source nodes
                const act = action;
                const parallelActions = act.parallel;
                // Store the starting nodes - all parallel branches connect FROM these
                const parallelStartNodes = [...currentNodeIds];
                // Collect the end nodes from all branches
                const allBranchEndNodes = [];
                // Parse each parallel branch - each starts from the same source
                for (const parallelItem of parallelActions) {
                    if (Array.isArray(parallelItem)) {
                        // It's a sequence array
                        const seqResult = this.parseActions(parallelItem, {
                            warnings,
                            previousNodeIds: parallelStartNodes,
                            getNextNodeId,
                            conditionNodeIds: localConditionNodeIds,
                            inheritedEnabled,
                        });
                        if (seqResult.nodes.length > 0) {
                            nodes.push(...seqResult.nodes);
                            edges.push(...seqResult.edges);
                            // Find the last nodes of this branch
                            const nodesWithOutgoing = new Set(seqResult.edges.map((e) => e.source));
                            const lastNodes = seqResult.nodes.filter((n) => !nodesWithOutgoing.has(n.id));
                            allBranchEndNodes.push(...lastNodes.map((n) => n.id));
                        }
                    }
                    else if (typeof parallelItem === 'object' && parallelItem !== null) {
                        const item = parallelItem;
                        if ('sequence' in item && Array.isArray(item.sequence)) {
                            // Nested sequence in parallel
                            const seqResult = this.parseActions(item.sequence, {
                                warnings,
                                previousNodeIds: parallelStartNodes,
                                getNextNodeId,
                                conditionNodeIds: localConditionNodeIds,
                                inheritedEnabled,
                            });
                            if (seqResult.nodes.length > 0) {
                                nodes.push(...seqResult.nodes);
                                edges.push(...seqResult.edges);
                                const nodesWithOutgoing = new Set(seqResult.edges.map((e) => e.source));
                                const lastNodes = seqResult.nodes.filter((n) => !nodesWithOutgoing.has(n.id));
                                allBranchEndNodes.push(...lastNodes.map((n) => n.id));
                            }
                        }
                        else {
                            // Single action in parallel - parse it as a single-item array
                            const singleResult = this.parseActions([parallelItem], {
                                warnings,
                                previousNodeIds: parallelStartNodes,
                                getNextNodeId,
                                conditionNodeIds: localConditionNodeIds,
                                inheritedEnabled,
                            });
                            if (singleResult.nodes.length > 0) {
                                nodes.push(...singleResult.nodes);
                                edges.push(...singleResult.edges);
                                // For a single action, the last node is just the last one parsed
                                const lastNode = singleResult.nodes[singleResult.nodes.length - 1];
                                allBranchEndNodes.push(lastNode.id);
                            }
                        }
                    }
                }
                // After parallel block, all branch end nodes become the current nodes
                // (subsequent actions will connect from all of them)
                currentNodeIds = allBranchEndNodes.length > 0 ? allBranchEndNodes : parallelStartNodes;
            }
            else if (isEventAction(action)) {
                // Event action - fires a Home Assistant event
                const nodeId = getNextNodeId('action');
                const act = action;
                const actionNode = {
                    id: nodeId,
                    type: 'action',
                    position: { x: 0, y: 0 },
                    data: {
                        alias: typeof act.alias === 'string' ? act.alias : undefined,
                        event: typeof act.event === 'string' ? act.event : undefined,
                        event_data: typeof act.event_data === 'object' && act.event_data !== null
                            ? act.event_data
                            : undefined,
                        enabled: getNodeEnabled(typeof act.enabled === 'boolean' ? act.enabled : undefined),
                    },
                };
                nodes.push(actionNode);
                createEdgesFromCurrent(nodeId);
                currentNodeIds = [nodeId];
            }
            else if (isRepeatAction(action)) {
                // Repeat block - explode into individual nodes with loop-back edges
                const act = action;
                const repeat = act.repeat;
                const repeatSequence = Array.isArray(repeat.sequence) ? repeat.sequence : [];
                const blockAlias = typeof act.alias === 'string' ? act.alias : undefined;
                const blockEnabled = getNodeEnabled(typeof act.enabled === 'boolean' ? act.enabled : undefined);
                if (Array.isArray(repeat.while) && repeat.while.length > 0) {
                    // ── repeat.while ──
                    // condition_node →(true)→ body... →(back-edge)→ condition_node
                    // condition_node →(false)→ [continues]
                    const whileConditions = repeat.while;
                    // Create condition nodes (chain them like if-block conditions)
                    const conditionNodes = [];
                    for (let ci = 0; ci < whileConditions.length; ci++) {
                        const condId = getNextNodeId('condition');
                        let parsedData;
                        try {
                            parsedData = HAConditionSchema.parse(whileConditions[ci]);
                        }
                        catch {
                            parsedData = {
                                condition: 'template',
                                value_template: JSON.stringify(whileConditions[ci]),
                            };
                        }
                        if (ci === 0 && blockAlias) {
                            parsedData.alias = blockAlias;
                        }
                        parsedData.enabled = blockEnabled;
                        const condNode = {
                            id: condId,
                            type: 'condition',
                            position: { x: 0, y: 0 },
                            data: parsedData,
                        };
                        conditionNodes.push(condNode);
                        nodes.push(condNode);
                        localConditionNodeIds.add(condId);
                    }
                    // Connect previous nodes → first condition
                    createEdgesFromCurrent(conditionNodes[0].id);
                    // Chain condition nodes together with 'true' edges
                    for (let ci = 0; ci < conditionNodes.length - 1; ci++) {
                        edges.push(this.createEdge(conditionNodes[ci].id, conditionNodes[ci + 1].id, 'true'));
                    }
                    const lastCondId = conditionNodes[conditionNodes.length - 1].id;
                    // Parse body sequence from last condition's TRUE path
                    const bodyResult = this.parseActions(repeatSequence, {
                        warnings,
                        previousNodeIds: [lastCondId],
                        getNextNodeId,
                        conditionNodeIds: localConditionNodeIds,
                        inheritedEnabled: blockEnabled,
                    });
                    nodes.push(...bodyResult.nodes);
                    edges.push(...bodyResult.edges);
                    // Fix the first edge from last condition to body to use 'true' handle
                    if (bodyResult.nodes.length > 0) {
                        const firstBodyId = bodyResult.nodes[0].id;
                        const trueEdge = edges.find((e) => e.source === lastCondId && e.target === firstBodyId);
                        if (trueEdge) {
                            trueEdge.sourceHandle = 'true';
                        }
                    }
                    // Find the last node in the body sequence
                    const bodyNodeIds = new Set(bodyResult.nodes.map((n) => n.id));
                    const bodySourceIds = new Set(bodyResult.edges.map((e) => e.source));
                    const bodyLastNodes = bodyResult.nodes.filter((n) => !bodySourceIds.has(n.id) ||
                        ![...bodyResult.edges].some((e) => e.source === n.id && bodyNodeIds.has(e.target)));
                    const lastBodyNodeId = bodyLastNodes.length > 0
                        ? bodyLastNodes[bodyLastNodes.length - 1].id
                        : bodyResult.nodes.length > 0
                            ? bodyResult.nodes[bodyResult.nodes.length - 1].id
                            : lastCondId;
                    // Create back-edge from last body node → first condition
                    if (bodyResult.nodes.length > 0) {
                        const backEdge = this.createEdge(lastBodyNodeId, conditionNodes[0].id);
                        edges.push(backEdge);
                    }
                    // Output continues from first condition's FALSE path
                    currentNodeIds = [conditionNodes[0].id];
                    falsePathConditionIds.add(conditionNodes[0].id);
                }
                else if ((Array.isArray(repeat.until) && repeat.until.length > 0) ||
                    typeof repeat.until === 'string') {
                    // ── repeat.until ──
                    // body... → condition_node →(true)→ [continues]
                    // condition_node →(false, back-edge)→ first body node
                    // Parse body sequence first
                    const bodyResult = this.parseActions(repeatSequence, {
                        warnings,
                        previousNodeIds: currentNodeIds,
                        getNextNodeId,
                        conditionNodeIds: localConditionNodeIds,
                        inheritedEnabled: blockEnabled,
                    });
                    nodes.push(...bodyResult.nodes);
                    edges.push(...bodyResult.edges);
                    // Find the first body node
                    const firstBodyNodeId = bodyResult.nodes.length > 0 ? bodyResult.nodes[0].id : null;
                    // Find the last body node
                    const bodyNodeIds = new Set(bodyResult.nodes.map((n) => n.id));
                    const bodySourceIds = new Set(bodyResult.edges.filter((e) => bodyNodeIds.has(e.target)).map((e) => e.source));
                    const bodyLastNodes = bodyResult.nodes.filter((n) => !bodySourceIds.has(n.id) ||
                        !bodyResult.edges.some((e) => e.source === n.id && bodyNodeIds.has(e.target)));
                    const lastBodyNodeId = bodyLastNodes.length > 0
                        ? bodyLastNodes[bodyLastNodes.length - 1].id
                        : bodyResult.nodes.length > 0
                            ? bodyResult.nodes[bodyResult.nodes.length - 1].id
                            : null;
                    // Create condition nodes from until conditions
                    const untilConditions = typeof repeat.until === 'string'
                        ? [{ condition: 'template', value_template: repeat.until }]
                        : repeat.until;
                    const conditionNodes = [];
                    for (let ci = 0; ci < untilConditions.length; ci++) {
                        const condId = getNextNodeId('condition');
                        let parsedData;
                        try {
                            parsedData = HAConditionSchema.parse(untilConditions[ci]);
                        }
                        catch {
                            parsedData = {
                                condition: 'template',
                                value_template: JSON.stringify(untilConditions[ci]),
                            };
                        }
                        if (ci === 0 && blockAlias && bodyResult.nodes.length === 0) {
                            parsedData.alias = blockAlias;
                        }
                        parsedData.enabled = blockEnabled;
                        const condNode = {
                            id: condId,
                            type: 'condition',
                            position: { x: 0, y: 0 },
                            data: parsedData,
                        };
                        conditionNodes.push(condNode);
                        nodes.push(condNode);
                        localConditionNodeIds.add(condId);
                    }
                    // Connect last body node → first condition
                    if (lastBodyNodeId) {
                        const lastBodyNode = bodyResult.nodes.find((n) => n.id === lastBodyNodeId);
                        const sourceHandle = lastBodyNode && localConditionNodeIds.has(lastBodyNodeId) ? 'true' : undefined;
                        edges.push(this.createEdge(lastBodyNodeId, conditionNodes[0].id, sourceHandle));
                    }
                    else {
                        // Empty body - connect previous nodes directly to condition
                        createEdgesFromCurrent(conditionNodes[0].id);
                    }
                    // Chain condition nodes together with 'true' edges
                    for (let ci = 0; ci < conditionNodes.length - 1; ci++) {
                        edges.push(this.createEdge(conditionNodes[ci].id, conditionNodes[ci + 1].id, 'true'));
                    }
                    const lastCondId = conditionNodes[conditionNodes.length - 1].id;
                    // Create back-edge from first condition →(false)→ first body node
                    if (firstBodyNodeId) {
                        const backEdge = this.createEdge(conditionNodes[0].id, firstBodyNodeId, 'false');
                        edges.push(backEdge);
                    }
                    // Output continues from last condition's TRUE path
                    currentNodeIds = [lastCondId];
                }
                else if (repeat.count !== undefined) {
                    // ── repeat.count ──
                    // set_vars(counter=0) → body... → set_vars(counter+1) → condition(counter < N)
                    //                        ↑                                     │(true)    │(false)
                    //                        └──── back-edge (repeatType=count) ──┘           → [continues]
                    const countValue = repeat.count;
                    const counterId = getNextNodeId('set_variables');
                    const counterVarName = `_repeat_counter_${counterId.replace(/[^a-zA-Z0-9_]/g, '_')}`;
                    // Create init set_variables node: counter = 0
                    const initNode = {
                        id: counterId,
                        type: 'set_variables',
                        position: { x: 0, y: 0 },
                        data: {
                            alias: blockAlias,
                            variables: { [counterVarName]: 0 },
                            enabled: blockEnabled,
                        },
                    };
                    nodes.push(initNode);
                    createEdgesFromCurrent(counterId);
                    // Parse body sequence
                    const bodyResult = this.parseActions(repeatSequence, {
                        warnings,
                        previousNodeIds: [counterId],
                        getNextNodeId,
                        conditionNodeIds: localConditionNodeIds,
                        inheritedEnabled: blockEnabled,
                    });
                    nodes.push(...bodyResult.nodes);
                    edges.push(...bodyResult.edges);
                    // Find last body node
                    const bodyNodeIds = new Set(bodyResult.nodes.map((n) => n.id));
                    const bodySourceIds = new Set(bodyResult.edges.filter((e) => bodyNodeIds.has(e.target)).map((e) => e.source));
                    const bodyLastNodes = bodyResult.nodes.filter((n) => !bodySourceIds.has(n.id) ||
                        !bodyResult.edges.some((e) => e.source === n.id && bodyNodeIds.has(e.target)));
                    const lastBodyNodeId = bodyLastNodes.length > 0
                        ? bodyLastNodes[bodyLastNodes.length - 1].id
                        : bodyResult.nodes.length > 0
                            ? bodyResult.nodes[bodyResult.nodes.length - 1].id
                            : counterId;
                    // Create increment set_variables node: counter = counter + 1
                    const incrId = getNextNodeId('set_variables');
                    const incrNode = {
                        id: incrId,
                        type: 'set_variables',
                        position: { x: 0, y: 0 },
                        data: {
                            variables: { [counterVarName]: `{{ ${counterVarName} + 1 }}` },
                            enabled: blockEnabled,
                        },
                    };
                    nodes.push(incrNode);
                    if (bodyResult.nodes.length > 0) {
                        const lastBodyNode = bodyResult.nodes.find((n) => n.id === lastBodyNodeId);
                        const sourceHandle = lastBodyNode && localConditionNodeIds.has(lastBodyNodeId) ? 'true' : undefined;
                        edges.push(this.createEdge(lastBodyNodeId, incrId, sourceHandle));
                    }
                    else {
                        edges.push(this.createEdge(counterId, incrId));
                    }
                    // Create condition node: counter < N
                    const condId = getNextNodeId('condition');
                    const condNode = {
                        id: condId,
                        type: 'condition',
                        position: { x: 0, y: 0 },
                        data: {
                            condition: 'template',
                            value_template: `{{ ${counterVarName} < ${countValue} }}`,
                            enabled: blockEnabled,
                        },
                    };
                    nodes.push(condNode);
                    localConditionNodeIds.add(condId);
                    edges.push(this.createEdge(incrId, condId));
                    // Back-edge: condition →(true)→ first body node (or init if no body)
                    const loopTargetId = bodyResult.nodes.length > 0 ? bodyResult.nodes[0].id : incrId;
                    const backEdge = this.createEdge(condId, loopTargetId, 'true');
                    edges.push(backEdge);
                    // Output continues from condition's FALSE path
                    currentNodeIds = [condId];
                    falsePathConditionIds.add(condId);
                }
                else {
                    // Unknown repeat type - create opaque action node as fallback
                    const nodeId = getNextNodeId('action');
                    const actionNode = {
                        id: nodeId,
                        type: 'action',
                        position: { x: 0, y: 0 },
                        data: {
                            alias: blockAlias,
                            repeat: repeat,
                            enabled: blockEnabled,
                        },
                    };
                    nodes.push(actionNode);
                    createEdgesFromCurrent(nodeId);
                    currentNodeIds = [nodeId];
                }
            }
            else if (isServiceAction(action)) {
                // Regular service call action (support both 'service' and 'action' fields)
                const nodeId = getNextNodeId('action');
                try {
                    const act = action;
                    // Use spread pattern to preserve unknown properties from custom integrations
                    const { alias, service, action: actionField, target, data, data_template, response_variable, continue_on_error, enabled, ...extraProps } = act;
                    const actionNode = {
                        id: nodeId,
                        type: 'action',
                        position: { x: 0, y: 0 },
                        data: {
                            ...extraProps, // Preserve extra properties
                            alias: typeof alias === 'string' ? alias : undefined,
                            service: typeof service === 'string'
                                ? service
                                : typeof actionField === 'string'
                                    ? actionField
                                    : undefined,
                            target: typeof target === 'object' && target !== null
                                ? target
                                : undefined,
                            data: typeof data === 'object' && data !== null
                                ? data
                                : undefined,
                            data_template: typeof data_template === 'object' && data_template !== null
                                ? data_template
                                : undefined,
                            response_variable: typeof response_variable === 'string' ? response_variable : undefined,
                            continue_on_error: typeof continue_on_error === 'boolean' ? continue_on_error : undefined,
                            enabled: getNodeEnabled(typeof enabled === 'boolean' ? enabled : undefined),
                        },
                    };
                    nodes.push(actionNode);
                    createEdgesFromCurrent(nodeId);
                    currentNodeIds = [nodeId];
                }
                catch (error) {
                    warnings.push(`Failed to parse action ${index}: ${error}`);
                    nodes.push(this.createUnknownNode(nodeId, action));
                }
            }
            else if (isSetConversationResponseAction(action)) {
                // set_conversation_response action - convert to service call format
                const nodeId = getNextNodeId('action');
                const act = action;
                const actionNode = {
                    id: nodeId,
                    type: 'action',
                    position: { x: 0, y: 0 },
                    data: {
                        alias: typeof act.alias === 'string' ? act.alias : undefined,
                        // Store the response as a special action
                        set_conversation_response: typeof act.set_conversation_response === 'string'
                            ? act.set_conversation_response
                            : undefined,
                        enabled: getNodeEnabled(typeof act.enabled === 'boolean' ? act.enabled : undefined),
                    },
                };
                nodes.push(actionNode);
                createEdgesFromCurrent(nodeId);
                currentNodeIds = [nodeId];
            }
            else if (isStopAction(action)) {
                // Stop action - halts automation execution
                const nodeId = getNextNodeId('action');
                const act = action;
                const actionNode = {
                    id: nodeId,
                    type: 'action',
                    position: { x: 0, y: 0 },
                    data: {
                        alias: typeof act.alias === 'string' ? act.alias : undefined,
                        stop: typeof act.stop === 'string' ? act.stop : '',
                        ...(act.error === true ? { error: true } : {}),
                        enabled: getNodeEnabled(typeof act.enabled === 'boolean' ? act.enabled : undefined),
                    },
                };
                nodes.push(actionNode);
                createEdgesFromCurrent(nodeId);
                currentNodeIds = [nodeId];
            }
            else {
                // Unknown action type - create unknown node
                warnings.push(`Unknown action type (${JSON.stringify(action)}) at index ${index}`);
                const nodeId = getNextNodeId('unknown');
                nodes.push({
                    id: nodeId,
                    type: 'action',
                    position: { x: 0, y: 0 },
                    data: {
                        alias: 'Unknown Node',
                        service: 'unknown.unknown',
                        data: action,
                    },
                });
                createEdgesFromCurrent(nodeId);
                currentNodeIds = [nodeId];
            }
        });
        return { nodes, edges, terminalNodeIds: currentNodeIds };
    }
    /**
     * Parse choose block (condition branching in actions)
     *
     * Home Assistant `choose` semantics:
     * - Evaluate conditions in order
     * - Execute ONLY the first matching branch's sequence
     * - If no conditions match, execute the default (if present)
     *
     * This creates a chain: condition1 → (true: seq1) → (false: condition2) → (true: seq2) → ... → default
     */
    parseChooseBlock(chooseAction, options) {
        const { warnings, previousNodeIds, getNextNodeId, conditionNodeIds = new Set(), falsePathConditionIds = new Set(), inheritedEnabled, } = options;
        const nodes = [];
        const edges = [];
        const outputNodeIds = [];
        const falsePathOutputIds = [];
        const localConditionIds = new Set(conditionNodeIds);
        // Compute effective enabled state: if parent is disabled or this block is disabled
        const blockEnabled = chooseAction.enabled;
        const effectiveEnabled = inheritedEnabled === false ? false : blockEnabled === false ? false : undefined;
        // Helper to get enabled state for nodes in this block
        const getNodeEnabled = () => effectiveEnabled;
        const choices = Array.isArray(chooseAction.choose)
            ? chooseAction.choose
            : [chooseAction.choose];
        // Filter to only valid choices with non-empty conditions.
        // Note: an empty array (`conditions: []`) is truthy in JS, so it must be
        // rejected explicitly by checking the array length.
        const validChoices = choices.filter((choice) => {
            if (typeof choice !== 'object' || choice === null)
                return false;
            const conds = choice.conditions;
            return Array.isArray(conds) ? conds.length > 0 : Boolean(conds);
        });
        // Track what nodes should connect to the next condition (false path of current)
        let currentPreviousIds = [...previousNodeIds];
        validChoices.forEach((choice, choiceIndex) => {
            // choice.conditions can be an array of conditions or a single condition object
            const conditionsArray = Array.isArray(choice.conditions)
                ? choice.conditions
                : [choice.conditions];
            // Create separate condition nodes for each condition in the choice (explode AND conditions)
            const choiceConditionNodes = [];
            for (let i = 0; i < conditionsArray.length; i++) {
                const condition = conditionsArray[i];
                const conditionId = getNextNodeId('condition');
                let conditionNode;
                if (condition && Array.isArray(condition.conditions)) {
                    // Condition with nested conditions (or/and/not) - preserve structure
                    const rawConditionType = condition.condition || 'and';
                    const conditionType = VALID_CONDITIONS.includes(rawConditionType)
                        ? rawConditionType
                        : 'template';
                    conditionNode = {
                        id: conditionId,
                        type: 'condition',
                        position: { x: 0, y: 0 },
                        data: {
                            // Only first condition in first choice gets the alias
                            alias: i === 0 ? choice.alias : undefined,
                            condition: conditionType,
                            conditions: transformConditions(condition.conditions),
                            // Preserve id for trigger conditions
                            id: condition.id,
                            enabled: getNodeEnabled(),
                        },
                    };
                }
                else {
                    // Simple condition - use Zod schema for parsing and type safety
                    const rawConditionType = condition?.condition || 'template';
                    const conditionType = VALID_CONDITIONS.includes(rawConditionType)
                        ? rawConditionType
                        : 'template';
                    // Build object with alias override for first condition
                    const looseObj = {
                        ...condition,
                        alias: i === 0 ? (choice.alias ?? condition?.alias) : condition?.alias,
                        condition: conditionType,
                        enabled: getNodeEnabled(),
                    };
                    // Validate and normalize with HAConditionSchema
                    let data;
                    try {
                        data = HAConditionSchema.parse(looseObj);
                    }
                    catch {
                        // Fallback: minimal valid template
                        data = {
                            alias: i === 0 ? choice.alias : undefined,
                            condition: 'template',
                            value_template: JSON.stringify(condition),
                            enabled: getNodeEnabled(),
                        };
                    }
                    conditionNode = {
                        id: conditionId,
                        type: 'condition',
                        position: { x: 0, y: 0 },
                        data,
                    };
                }
                choiceConditionNodes.push(conditionNode);
                nodes.push(conditionNode);
                localConditionIds.add(conditionId);
            }
            // Guard: skip this choice entirely if no condition nodes were created
            // (prevents "Cannot read properties of undefined (reading 'id')").
            if (choiceConditionNodes.length === 0)
                return;
            const firstConditionId = choiceConditionNodes[0].id;
            const lastConditionId = choiceConditionNodes[choiceConditionNodes.length - 1].id;
            // Connect from current previous nodes to first condition of this choice
            // For first choice, connect from original previousNodeIds
            // For subsequent choices, connect from previous choice's first condition's FALSE path
            for (const prevId of currentPreviousIds) {
                let sourceHandle;
                if (choiceIndex > 0 && localConditionIds.has(prevId) && !conditionNodeIds.has(prevId)) {
                    // Previous is a condition from this choose block - use FALSE path
                    sourceHandle = 'false';
                }
                else if (falsePathConditionIds.has(prevId)) {
                    // Previous is a condition whose FALSE path should connect here
                    sourceHandle = 'false';
                }
                else if (conditionNodeIds.has(prevId)) {
                    // Previous is an external condition (e.g., root-level) - use TRUE path
                    sourceHandle = 'true';
                }
                // else: previous is not a condition - no sourceHandle needed
                edges.push(this.createEdge(prevId, firstConditionId, sourceHandle));
            }
            // Chain condition nodes together with 'true' edges
            for (let i = 0; i < choiceConditionNodes.length - 1; i++) {
                edges.push(this.createEdge(choiceConditionNodes[i].id, choiceConditionNodes[i + 1].id, 'true'));
            }
            // Parse sequence for this choice (TRUE path from last condition)
            if (choice.sequence) {
                const sequence = Array.isArray(choice.sequence) ? choice.sequence : [choice.sequence];
                const sequenceResult = this.parseActions(sequence, {
                    warnings,
                    previousNodeIds: [lastConditionId],
                    getNextNodeId,
                    conditionNodeIds: localConditionIds,
                    inheritedEnabled: effectiveEnabled,
                });
                nodes.push(...sequenceResult.nodes);
                edges.push(...sequenceResult.edges);
                // Connect last condition node to first action in sequence via 'true' handle
                if (sequenceResult.nodes.length > 0) {
                    const firstActionId = sequenceResult.nodes[0].id;
                    const trueEdge = edges.find((e) => e.source === lastConditionId && e.target === firstActionId);
                    if (trueEdge) {
                        trueEdge.sourceHandle = 'true';
                    }
                    // The last node in the sequence is the output
                    const lastNodeId = sequenceResult.nodes[sequenceResult.nodes.length - 1].id;
                    outputNodeIds.push(lastNodeId);
                }
                else {
                    // Empty sequence - last condition itself is output
                    outputNodeIds.push(lastConditionId);
                }
            }
            else {
                // No sequence - the last condition's true path is an output
                outputNodeIds.push(lastConditionId);
            }
            // Next choice connects from this choice's FIRST condition's FALSE path
            // (If any condition in the chain fails, we skip to the next choice)
            currentPreviousIds = [firstConditionId];
        });
        // Handle default sequence (connects from last condition's FALSE path)
        if (chooseAction.default) {
            const defaultSequence = Array.isArray(chooseAction.default)
                ? chooseAction.default
                : [chooseAction.default];
            const defaultResult = this.parseActions(defaultSequence, {
                warnings,
                previousNodeIds: currentPreviousIds,
                getNextNodeId,
                conditionNodeIds: localConditionIds,
                inheritedEnabled: effectiveEnabled,
            });
            nodes.push(...defaultResult.nodes);
            edges.push(...defaultResult.edges);
            // Connect from last condition's FALSE path to default
            if (currentPreviousIds.length > 0 && defaultResult.nodes.length > 0) {
                const lastConditionId = currentPreviousIds[0];
                const firstDefaultId = defaultResult.nodes[0].id;
                const falseEdge = edges.find((e) => e.source === lastConditionId && e.target === firstDefaultId);
                if (falseEdge && localConditionIds.has(lastConditionId)) {
                    falseEdge.sourceHandle = 'false';
                }
                // The last node in the default sequence is the output
                const lastNodeId = defaultResult.nodes[defaultResult.nodes.length - 1].id;
                outputNodeIds.push(lastNodeId);
            }
        }
        else if (validChoices.length > 0) {
            // No default - the last condition's false path is an implicit output
            // (the automation continues after the choose if no condition matches)
            const lastConditionId = currentPreviousIds[0];
            outputNodeIds.push(lastConditionId);
            // Track that this output should use FALSE path, not TRUE
            falsePathOutputIds.push(lastConditionId);
        }
        return { nodes, edges, outputNodeIds, falsePathOutputIds };
    }
    /**
     * Parse if/then/else block
     */
    parseIfBlock(ifAction, options) {
        const { warnings, previousNodeIds, getNextNodeId, conditionNodeIds = new Set(), falsePathConditionIds: incomingFalsePathIds = new Set(), triggerNodeMap, inheritedEnabled, } = options;
        const nodes = [];
        const edges = [];
        const outputNodeIds = [];
        const falsePathOutputIds = [];
        const localConditionIds = new Set(conditionNodeIds);
        // Compute effective enabled state: if parent is disabled or this block is disabled
        const effectiveEnabled = inheritedEnabled === false ? false : ifAction.enabled === false ? false : undefined;
        // Helper to get enabled state for nodes in this block
        const getNodeEnabled = () => effectiveEnabled;
        const ifConditions = Array.isArray(ifAction.if) ? ifAction.if : [ifAction.if];
        // Create separate condition nodes for each condition in the if: array
        // This "explodes" combined conditions into separate linked nodes
        const conditionNodes = [];
        for (let i = 0; i < ifConditions.length; i++) {
            const condition = ifConditions[i];
            const conditionId = getNextNodeId('condition');
            let conditionNode;
            if (condition && Array.isArray(condition.conditions)) {
                // Condition with nested conditions (or/and/not) - preserve structure
                const rawConditionType = condition.condition || 'and';
                const conditionType = VALID_CONDITIONS.includes(rawConditionType)
                    ? rawConditionType
                    : 'template';
                conditionNode = {
                    id: conditionId,
                    type: 'condition',
                    position: { x: 0, y: 0 },
                    data: markConditionInSequence({
                        // Only first condition gets the alias from ifAction
                        alias: i === 0 ? ifAction.alias : undefined,
                        condition: conditionType,
                        conditions: transformConditions(condition.conditions),
                        enabled: getNodeEnabled(),
                    }),
                };
            }
            else {
                // Simple condition - use its properties directly
                const rawConditionType = condition?.condition || 'numeric_state';
                const conditionType = VALID_CONDITIONS.includes(rawConditionType)
                    ? rawConditionType
                    : 'template';
                // Use Zod looseObject for normalization and type safety
                const looseObj = {
                    ...condition,
                    // Only first condition gets the alias from ifAction
                    alias: i === 0 ? (ifAction.alias ?? condition?.alias) : condition?.alias,
                    condition: conditionType,
                    enabled: getNodeEnabled(),
                };
                // Validate and normalize with HAConditionSchema
                let data;
                try {
                    data = HAConditionSchema.parse(looseObj);
                }
                catch {
                    // Fallback: minimal valid template
                    data = {
                        alias: i === 0 ? ifAction.alias : undefined,
                        condition: 'template',
                        value_template: JSON.stringify(condition),
                        enabled: getNodeEnabled(),
                    };
                }
                conditionNode = {
                    id: conditionId,
                    type: 'condition',
                    position: { x: 0, y: 0 },
                    data: markConditionInSequence(data),
                };
            }
            conditionNodes.push(conditionNode);
            nodes.push(conditionNode);
            localConditionIds.add(conditionId);
        }
        // Connect from previous nodes to the first condition.
        // Special case: if this is a single trigger-id condition (no else), only connect
        // the trigger(s) whose id matches — this creates independent parallel flows instead
        // of a single chained sequence when multiple if-trigger blocks exist.
        const firstConditionId = conditionNodes[0].id;
        // Detect trigger-id routing: a single `condition: trigger` with no else.
        // The `id` field can be a string or an array of strings in HA YAML.
        const triggerConditionIds = (() => {
            if (ifAction.else || ifConditions.length !== 1)
                return null;
            const cond = ifConditions[0];
            if (cond?.condition !== 'trigger')
                return null;
            const rawId = cond?.id;
            if (typeof rawId === 'string')
                return [rawId];
            if (Array.isArray(rawId) && rawId.length > 0 && rawId.every((x) => typeof x === 'string'))
                return rawId;
            return null;
        })();
        for (const prevId of previousNodeIds) {
            // If this is a trigger-id condition and we have trigger routing info,
            // only connect triggers whose id is listed in this condition's id array.
            if (triggerConditionIds !== null && triggerNodeMap) {
                const triggerIdForNode = triggerNodeMap.get(prevId);
                if (triggerIdForNode !== undefined && !triggerConditionIds.includes(triggerIdForNode)) {
                    // This trigger's id doesn't match — don't connect it here
                    continue;
                }
            }
            let sourceHandle;
            if (incomingFalsePathIds.has(prevId)) {
                sourceHandle = 'false';
            }
            else if (localConditionIds.has(prevId)) {
                sourceHandle = 'true';
            }
            edges.push(this.createEdge(prevId, firstConditionId, sourceHandle));
        }
        // Chain condition nodes together with 'true' edges
        for (let i = 0; i < conditionNodes.length - 1; i++) {
            edges.push(this.createEdge(conditionNodes[i].id, conditionNodes[i + 1].id, 'true'));
        }
        // The last condition node connects to the 'then' actions
        const lastConditionId = conditionNodes[conditionNodes.length - 1].id;
        // Parse 'then' sequence (true branch) - connects from last condition
        if (ifAction.then) {
            const thenSequence = Array.isArray(ifAction.then) ? ifAction.then : [ifAction.then];
            const thenResult = this.parseActions(thenSequence, {
                warnings,
                previousNodeIds: [lastConditionId],
                getNextNodeId,
                conditionNodeIds: localConditionIds,
                inheritedEnabled: effectiveEnabled,
            });
            nodes.push(...thenResult.nodes);
            edges.push(...thenResult.edges);
            // The edges from last condition to first action should use 'true' handle
            if (thenResult.nodes.length > 0) {
                const firstActionId = thenResult.nodes[0].id;
                const trueEdge = edges.find((e) => e.source === lastConditionId && e.target === firstActionId);
                if (trueEdge) {
                    trueEdge.sourceHandle = 'true';
                }
            }
            // Track all terminal nodes from then branch (not just the last created node,
            // as the last action in the sequence may itself be an if/then/else with multiple exits)
            outputNodeIds.push(...thenResult.terminalNodeIds);
        }
        // Parse 'else' sequence (false branch) - connects from FIRST condition only
        // (This matches the expected behavior: only the first condition handles the else path)
        if (ifAction.else) {
            const elseSequence = Array.isArray(ifAction.else) ? ifAction.else : [ifAction.else];
            // For else branch, we need to connect from first condition with 'false' handle
            const elseResult = this.parseActions(elseSequence, {
                warnings,
                previousNodeIds: [firstConditionId],
                getNextNodeId,
                conditionNodeIds: new Set(), // Don't use localConditionIds for else - we handle the edge manually
                inheritedEnabled: effectiveEnabled,
            });
            nodes.push(...elseResult.nodes);
            // Add edges manually with 'false' handle for first connection
            if (elseResult.nodes.length > 0) {
                const firstElseNodeId = elseResult.nodes[0].id;
                // Remove any auto-generated edges from first condition to first else node
                const existingEdgeIndex = elseResult.edges.findIndex((e) => e.source === firstConditionId && e.target === firstElseNodeId);
                if (existingEdgeIndex >= 0) {
                    elseResult.edges.splice(existingEdgeIndex, 1);
                }
                // Add edge with 'false' handle
                edges.push(this.createEdge(firstConditionId, firstElseNodeId, 'false'));
            }
            // Add remaining edges from else result
            edges.push(...elseResult.edges);
            // Track all terminal nodes from else branch
            outputNodeIds.push(...elseResult.terminalNodeIds);
        }
        else if (triggerConditionIds !== null) {
            // Trigger-id routing: this if block is a dedicated branch for one trigger.
            // There is no sequential false-path continuation — subsequent if blocks are
            // independent branches, each connected directly from their matching trigger.
            // Don't add condition nodes to outputs; they are leaf nodes for this branch.
        }
        else {
            // No else branch: every condition node is an implicit false exit.
            // The first condition's false path skips the entire if block; each subsequent
            // condition in the AND-chain also exits false when it fails.
            for (const condNode of conditionNodes) {
                outputNodeIds.push(condNode.id);
                falsePathOutputIds.push(condNode.id);
            }
        }
        // If no outputs were added (empty then + else branch), the last condition is the output
        if (outputNodeIds.length === 0 && triggerConditionIds === null) {
            outputNodeIds.push(lastConditionId);
            falsePathOutputIds.push(lastConditionId);
        }
        // For trigger-id routing: the trigger nodes that were NOT consumed by this if block
        // must remain available for subsequent if blocks.
        const unconsumedPreviousIds = triggerConditionIds !== null && triggerNodeMap
            ? previousNodeIds.filter((id) => {
                const triggerId = triggerNodeMap.get(id);
                // Keep: trigger nodes whose id is not in this condition's id list, OR non-trigger nodes
                return triggerId === undefined || !triggerConditionIds.includes(triggerId);
            })
            : [];
        return { nodes, edges, outputNodeIds, falsePathOutputIds, unconsumedPreviousIds };
    }
    /**
     * Create an unknown node for unparseable content
     */
    createUnknownNode(nodeId, originalData) {
        const data = originalData;
        return {
            id: nodeId,
            type: 'action',
            position: { x: 0, y: 0 },
            data: {
                alias: `Unknown: ${data?.service || data?.trigger || 'Node'}`,
                service: data?.service || 'unknown.unknown',
                data: data,
            },
        };
    }
    /**
     * Apply positions from metadata
     */
    applyMetadataPositions(nodes, metadata) {
        return nodes.map((node) => ({
            ...node,
            position: metadata.nodes[node.id] || node.position,
        }));
    }
    /**
     * Create an edge between two nodes
     */
    createEdge(source, target, sourceHandle) {
        return {
            id: generateEdgeId(source, target),
            source,
            target,
            sourceHandle: sourceHandle || undefined,
        };
    }
}
// Export singleton instance
export const yamlParser = new YamlParser();
//# sourceMappingURL=YamlParser.js.map